/**
 * SEP session handling (doc 04).
 *
 * The token arrives as `?jwt=` on first load (SEP redirects the user in), is
 * then kept for the browser and forwarded as a bearer header on every API call.
 *
 * It is NOT validated here. The backend validates it against SEP on every
 * request; this side only reacts to the verdict. The SEP frontend used to
 * validate up front instead — an extra round trip before every request that
 * secured nothing, because the backend trusted the token regardless.
 */
import type { Identity } from '@/types/api';

const STORAGE_KEY = 'sep.jwt';

export const SEP_LOGIN_URL = 'https://sep.energyapps.ch/user/account/';

/**
 * Three-valued on purpose: "rejected" and "SEP is down" have opposite
 * consequences, and a valid token from the wrong tenant is a fourth thing again.
 */
export type AuthVerdict =
  | { kind: 'ok'; identity: Identity }
  | { kind: 'invalid' }
  | { kind: 'forbidden'; identity: Identity }
  | { kind: 'unavailable' };

/**
 * localStorage, not sessionStorage: the token should survive a new tab and a
 * browser restart. With sessionStorage every new tab is an empty session that
 * bounces the user back to SEP although they are signed in.
 */
export function storedToken(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function storeToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* private mode — the session simply does not persist */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

let activeToken = '';

export function currentToken(): string {
  return activeToken;
}

export function setActiveToken(token: string): void {
  activeToken = token;
}

/**
 * Take the token out of the address bar immediately — even one that turns out
 * to be invalid has no business in history, bookmarks or a referrer header.
 * Other deep-link parameters are preserved.
 */
export function stripJwtFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('jwt')) return;
    url.searchParams.delete('jwt');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {
    /* ignore */
  }
}

export function tokenFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('jwt') ?? '';
  } catch {
    return '';
  }
}

/**
 * The `?jwt=` handover, captured exactly once.
 *
 * Reading it is destructive — we strip it from the address bar immediately — so
 * a second read finds nothing. React StrictMode mounts effects twice in
 * development, which made the second boot attempt conclude the user had never
 * signed in and bounce them to SEP while the first attempt's probe was still in
 * flight. Capturing into a module-level variable makes the handover survive any
 * number of mounts.
 */
let capturedUrlToken: string | null = null;

function captureUrlToken(): string {
  if (capturedUrlToken === null) {
    capturedUrlToken = tokenFromUrl();
    if (capturedUrlToken) stripJwtFromUrl();
  }
  return capturedUrlToken;
}

/**
 * Navigation seam. Only exists so tests can observe a redirect instead of
 * jsdom refusing to navigate.
 */
export const nav = {
  replace(url: string): void {
    window.location.replace(url);
  },
};

let redirecting = false;

/** Drop the local session and hand the user back to SEP. Once, not once per
 * in-flight request. */
export function redirectToLogin(): void {
  if (redirecting) return;
  redirecting = true;
  clearToken();
  setActiveToken('');
  nav.replace(SEP_LOGIN_URL);
}

/**
 * Probe one candidate token against our own backend, which validates it against
 * SEP. `/me` answers 200 for any SEP user (not only admins) so a rejected tenant
 * can be named on the no-access screen rather than guessed at.
 */
export async function probe(token: string): Promise<AuthVerdict> {
  if (!token) return { kind: 'invalid' };
  let response: Response;
  try {
    response = await fetch('/api/v1/me', {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
  } catch {
    return { kind: 'unavailable' };
  }

  if (response.status === 401) return { kind: 'invalid' };
  if (!response.ok) return { kind: 'unavailable' };

  const identity = (await response.json()) as Identity;
  return identity.isAdmin ? { kind: 'ok', identity } : { kind: 'forbidden', identity };
}

/**
 * The boot sequence: the token SEP just issued first, then the stored one. The
 * first the backend accepts wins. We only go back to SEP if every candidate was
 * rejected — a stored, valid token means `?jwt=` is never needed again.
 */
async function runAuthentication(): Promise<AuthVerdict> {
  const fromUrl = captureUrlToken();

  const candidates = [fromUrl, storedToken()].filter(
    (token, index, all): token is string => Boolean(token) && all.indexOf(token) === index,
  );

  if (candidates.length === 0) {
    redirectToLogin();
    return { kind: 'invalid' };
  }

  let lastVerdict: AuthVerdict = { kind: 'invalid' };
  for (const candidate of candidates) {
    const verdict = await probe(candidate);
    if (verdict.kind === 'ok') {
      setActiveToken(candidate);
      storeToken(candidate);
      return verdict;
    }
    if (verdict.kind === 'forbidden') {
      // A valid token that simply is not allowed in. Do not store it, do not
      // redirect — SEP would hand back the very same token and we would loop.
      return verdict;
    }
    if (verdict.kind === 'unavailable') {
      // An outage says nothing about the token: keep what is stored and show
      // the outage instead of signing the user out.
      return verdict;
    }
    lastVerdict = verdict;
  }

  clearToken();
  redirectToLogin();
  return lastVerdict;
}

/**
 * Memoised so every caller shares one boot attempt.
 *
 * Signing in is a one-shot, side-effecting sequence — it consumes the `?jwt=`
 * handover and can redirect. Running it twice (StrictMode, a remount, two
 * components asking at once) must not mean two attempts racing each other.
 */
let authAttempt: Promise<AuthVerdict> | null = null;

export function authenticate(): Promise<AuthVerdict> {
  authAttempt ??= runAuthentication();
  return authAttempt;
}

export function signOut(): void {
  clearToken();
  setActiveToken('');
  nav.replace(SEP_LOGIN_URL);
}

/** Test seam: clear the module-level state between cases. */
export function __resetSessionForTests(): void {
  capturedUrlToken = null;
  authAttempt = null;
  activeToken = '';
  redirecting = false;
}
